import { ROUTER_KEY } from '../../router/routers';
import { SVGImageComponentReact } from '../../themes/Images';

/**
 * MenuApp - Returns complete navigation menu structure
 * 
 * Generates an array of menu item objects with routing and permission metadata.
 * Menu items include:
 * - Orders: Main order management
 * - Dashboard: All locations overview (admin-only)
 * - Service: Service management (admin-only)
 * - Vendor Notes: Notes and communications
 * - Analytics: Business analytics and reporting
 * - Quotes: Accepted quotes management
 * - Team: Team member management (admin-only)
 * - Subscription: Subscription management (admin-only)
 * - Payment Processing: Stripe payment integration (admin-only)
 * - Branding: White-label branding settings (admin + parent account only)
 * - RoleManagement: Role and permissions management
 * 
 * Permission Flags:
 * - isSuperUser: Available to all authenticated users
 * - isAdminOnly: Only visible to account administrators
 * - isParentAccountOnly: Only visible to parent accounts (not child accounts)
 * - isChildBranding: Child account branding management (requires manage_branding permission)
 * 
 * Each menu item includes:
 * - key: Unique identifier
 * - title: Translation key for menu label
 * - route: Router key for navigation
 * - icon: Inactive state icon component
 * - icon_active: Active state icon component
 * - color: Visual color anchor for UI styling
 * - Permission flags (isSuperUser, isAdminOnly, etc.)
 * 
 * @returns {Array<Object>} Menu configuration array
 */
export const MenuApp = () => {
  return [
    {
      key: 'Orders',
      title: 'orders',
      route: ROUTER_KEY.order,
      icon: SVGImageComponentReact.IconMenuOrder,
      icon_active: SVGImageComponentReact.IconOrderActive,
      color: '#FF0000', // System Red
      isSuperUser: true,
      permissionCodename: 'app_perm_read_order_list',
      editPermissionCodename: 'app_perm_edit_order_list',
    },
    {
      key: "Dashboard",
      title: "allLocationOrders",
      route: ROUTER_KEY.dashboard,
      icon: SVGImageComponentReact.IconDashBoardApp,
      icon_active: SVGImageComponentReact.IconDashBoardActiveApp,
      color: '#0066FF', // System Blue
      isAdminOnly: true,
      isParentAccountOnly: true,
      isSuperUser: true,
      permissionCodename: 'app_perm_read_all_location_order_list',
      editPermissionCodename: 'app_perm_edit_all_location_order_list',
    },
    {
      key: 'serviceOrders',
      title: 'serviceOrders',
      route: ROUTER_KEY.service,
      icon: SVGImageComponentReact.ServiceIcon,
      icon_active: SVGImageComponentReact.ServiceIconActive,
      color: '#00A651', // System Green
      isAdminOnly: true,
      isParentAccountOnly: true,
      isSuperUser: true,
      permissionCodename: 'app_perm_read_service_order_list',
      editPermissionCodename: 'app_perm_edit_service_order_list',
    },
    {
      key: 'Vendor Notes',
      title: 'vendorNotes',
      route: ROUTER_KEY.notes,
      icon: SVGImageComponentReact.IconNotes,
      icon_active: SVGImageComponentReact.IconNotesActive,
      color: '#FFD700', // System Yellow
      isSuperUser: true,
      permissionCodename: 'app_perm_read_vendor_notes',
      editPermissionCodename: 'app_perm_edit_vendor_notes',
    },
    {
      key: 'Analytics',
      title: 'analytics',
      route: ROUTER_KEY.analytics,
      icon: SVGImageComponentReact.IconChart,
      icon_active: SVGImageComponentReact.IconChartActive,
      color: '#FF7A00', // System Orange
      isSuperUser: true,
      permissionCodename: 'app_perm_read_analytics',
      editPermissionCodename: 'app_perm_edit_analytics',
    },
    {
      key: 'Quotes',
      title: 'acceptedQuotes',
      route: ROUTER_KEY.quotes,
      icon: SVGImageComponentReact.IconQuotes,
      icon_active: SVGImageComponentReact.IconQuotesActive,
      color: '#8000FF', // System Purple
      isSuperUser: true,
      permissionCodename: 'app_perm_read_accepted_quote_list',
      editPermissionCodename: 'app_perm_edit_accepted_quote_list',
    },
    {
      key: 'Team',
      title: 'team',
      route: ROUTER_KEY.team,
      icon: SVGImageComponentReact.TeamIcon,
      icon_active: SVGImageComponentReact.TeamIconActive,
      color: '#FF4DA6', // System Pink
      isAdminOnly: true,
      isSuperUser: true,
      permissionCodename: 'app_perm_read_team',
      editPermissionCodename: 'app_perm_edit_team',
    },
    {
      key: 'Subscription',
      title: 'subscription',
      route: ROUTER_KEY.subscription,
      icon: SVGImageComponentReact.SubscriptionIcon,
      icon_active: SVGImageComponentReact.SubscriptionActiveIcon,
      color: '#00CFFF', // System Cyan
      isAdminOnly: true,
      isSuperUser: true,
      permissionCodename: 'app_perm_read_subscriptions',
      editPermissionCodename: 'app_perm_edit_subscriptions',
    },
    {
      key: 'ConnectStripe',
      title: 'paymentProcessing',
      route: ROUTER_KEY.connectStripe,
      icon: SVGImageComponentReact.PaymentIcon,
      icon_active: SVGImageComponentReact.PaymentActiveIcon,
      color: '#99CC00', // System Lime
      isAdminOnly: true,
      isSuperUser: true,
      permissionCodename: 'app_perm_read_payment_processing',
      editPermissionCodename: 'app_perm_edit_payment_processing',
    },
    {
      key: 'branding',
      title: 'branding',
      route: ROUTER_KEY.whiteLabel,
      icon: SVGImageComponentReact.WhiteLable,
      icon_active: SVGImageComponentReact.WhiteLableActive,
      color: '#8B4513', // System Brown
      isAdminOnly: true,
      isParentAccountOnly: true,
      isChildBranding: true,
      isSuperUser: true,
      permissionCodename: 'app_perm_read_branding',
      editPermissionCodename: 'app_perm_edit_branding',
    },
    {
      key: 'RoleManagement',
      title: 'Role Management',
      route: ROUTER_KEY.roleManagement,
      icon: SVGImageComponentReact.RolesManagementIcon,
      icon_active: SVGImageComponentReact.RolesManagementIcon,
      color: '#000000', // System Black
      isSuperUser: true,
      requireRoleManagementAccess: true,
    },
  ];
};
